import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  RotateCcw,
  TrendingUp,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useBalance, useTransactions, type Transaction } from '@/hooks/useBalance';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { EmptyState, ErrorState } from '@mustauto/ui/web';
import { formatReais } from '@/lib/utils';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';

const TX_TYPE_CONFIG: Record<string, { label: string; icon: typeof ArrowDownCircle; color: string }> = {
  deposit: { label: 'Depósito', icon: ArrowDownCircle, color: 'text-green-600 dark:text-green-400' },
  consumption: { label: 'Consumo', icon: ArrowUpCircle, color: 'text-red-500 dark:text-red-400' },
  refund: { label: 'Reembolso', icon: RotateCcw, color: 'text-amber-600 dark:text-amber-400' },
  auto_renewal: { label: 'Recarga automática', icon: RefreshCw, color: 'text-blue-600 dark:text-blue-400' },
};

const TX_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  completed: 'success',
  pending: 'warning',
  processing: 'warning',
  failed: 'destructive',
  refunded: 'secondary',
};

function TransactionRow({ tx }: { tx: Transaction }) {
  const config = TX_TYPE_CONFIG[tx.type] ?? TX_TYPE_CONFIG['deposit'];
  const Icon = config.icon;
  const isCredit = tx.type === 'deposit' || tx.type === 'auto_renewal' || tx.type === 'refund';

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Icon size={16} className={config.color} />
          <span className="text-sm font-medium">{config.label}</span>
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {tx.description ?? '—'}
      </TableCell>
      <TableCell className="text-right">
        <span className={`text-sm font-semibold ${isCredit ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
          {isCredit ? '+' : '-'}{formatReais(tx.amount)}
        </span>
      </TableCell>
      <TableCell className="text-right text-xs text-muted-foreground">
        {formatReais(tx.balanceAfter)}
      </TableCell>
      <TableCell>
        <Badge variant={TX_STATUS_VARIANT[tx.status] ?? 'secondary'}>
          {tx.status === 'completed' ? 'Confirmado' : tx.status === 'pending' ? 'Pendente' : tx.status === 'failed' ? 'Falhou' : tx.status}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(tx.createdAt).toLocaleDateString('pt-BR')}
      </TableCell>
    </TableRow>
  );
}

export function BalancePage() {
  const {
    data: balance,
    isLoading: loadingBalance,
    isError: balanceError,
    error: balanceErrorObj,
    refetch: refetchBalance,
  } = useBalance();
  const [txPage, setTxPage] = useState(1);
  const {
    data: txData,
    isLoading: loadingTx,
    isError: txError,
    error: txErrorObj,
    refetch: refetchTx,
  } = useTransactions(txPage);

  if (loadingBalance) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // O gate acima olhava so o carregando, e logo abaixo vinha `const b = balance!`.
  // Com a busca falhada `balance` e undefined e a primeira leitura de
  // `b.currentBalance` lanca — a tela do saldo nao ficava incompleta, ela
  // CAIA no error boundary ("Algo deu errado", sem retry e sem contexto).
  if (!balance) {
    return (
      <ErrorState
        title="Não foi possível carregar seu saldo"
        error={balanceErrorObj}
        detail={balanceError ? undefined : 'Tente novamente em instantes.'}
        onRetry={() => void refetchBalance()}
      />
    );
  }

  const b = balance;

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Saldo</h1>
          <p className="text-sm text-muted-foreground">Gerencie seu saldo e acompanhe transações</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/balance/projection"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
          >
            <TrendingUp size={14} />
            Simular investimento
          </Link>
          <Link
            to="/balance/top-up"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Wallet size={14} />
            Depositar
          </Link>
        </div>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Saldo atual</div>
            <div className="text-3xl font-bold text-primary">{formatReais(b.currentBalance)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total depositado</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatReais(b.totalDeposited)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total consumido</div>
            <div className="text-2xl font-bold text-red-500 dark:text-red-400">{formatReais(b.totalConsumed)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Auto-renewal status */}
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <RefreshCw size={18} className="text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">Recarga automática</div>
              <div className="text-xs text-muted-foreground">
                {b.autoRenewal.enabled
                  ? `Ativada — R$${b.autoRenewal.amount?.toLocaleString('pt-BR')} quando saldo < R$${b.autoRenewal.threshold?.toLocaleString('pt-BR')}`
                  : 'Desativada'}
              </div>
            </div>
          </div>
          <Link
            to="/settings"
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            Configurar
          </Link>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Transações</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTx ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Carregando...</div>
          ) : txError && !txData ? (
            /* "Nenhuma transação · Faça seu primeiro depósito" para quem ja
               recarregou e tem extrato e uma afirmacao sobre o dinheiro dele. */
            <ErrorState
              title="Não foi possível carregar suas transações"
              error={txErrorObj}
              onRetry={() => void refetchTx()}
            />
          ) : !txData || txData.items.length === 0 ? (
            <EmptyState
              icon={<Wallet size={28} />}
              title="Nenhuma transação"
              description="Suas transações aparecerão aqui após o primeiro depósito."
              action={{ label: 'Fazer primeiro depósito', href: '/balance/top-up' }}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Saldo após</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txData.items.map((tx) => (
                    <TransactionRow key={tx.id} tx={tx} />
                  ))}
                </TableBody>
              </Table>

              {txData.total > 20 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                    disabled={txPage === 1}
                    className="rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <span className="text-xs text-muted-foreground">
                    Página {txPage} de {Math.ceil(txData.total / 20)}
                  </span>
                  <button
                    onClick={() => setTxPage((p) => p + 1)}
                    disabled={txPage >= Math.ceil(txData.total / 20)}
                    className="rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
